import { useId } from 'react';
import { cx } from '@/utils/cx';
import styles from '@/styles/components/SegmentedControl/style.scss';

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
}

interface SegmentedControlProps<T extends string> {
  legend: string;
  options: SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Fixed column count; defaults to one row that wraps. */
  columns?: number;
  /** Keep the legend for screen readers only (e.g. filter buttons whose labels speak for themselves). */
  hideLegend?: boolean;
}

/** A radio group drawn as one-tap buttons. */
export function SegmentedControl<T extends string>({ legend, options, value, onChange, columns, hideLegend }: SegmentedControlProps<T>) {
  const name = useId();
  return (
    <fieldset className={styles.fieldset}>
      <legend className={cx(styles.legend, hideLegend && styles.srOnly)}>{legend}</legend>
      <div
        className={cx(styles.options, columns ? styles.fixed : undefined)}
        style={columns ? { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` } : undefined}
      >
        {options.map((o) => (
          <label key={o.value} className={cx(styles.option, o.value === value && styles.selected)}>
            <input
              type="radio"
              name={name}
              value={o.value}
              checked={o.value === value}
              onChange={() => onChange(o.value)}
              className={styles.input}
            />
            {o.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}
